import React, { createContext, useCallback, useState, useEffect } from 'react';
import { Buffer } from 'buffer';
import { TripleDES, enc } from 'crypto-js';
import { MessageBarType } from '@fluentui/react';
import { CURRENT_OFFICE_THEME } from '../shared/styles';
import { CMIC_TREE_NODE_TYPES, CMIC_KEYS, ACTION_ROLE } from '../shared/Constants';
import {
	getUsernameAndTenantId,
	getEnvironmentNameFromWSURL,
	checkParentAddittion,
	handleBreadCrumbsAddittion,
} from '../shared/UtilityFunctions';
import { fetchData } from '../utility/fetchData';

// Initial data

const INIT_USER_INFO = {
	username: '',
	tenantID: '',
	appId: '',
	password: '',
	serverURL: '',
	usernameAndTenantId: '',
	environmentName: '',
	serverType: { key: '', label: '' },
	isTreeShown: 0,
	actionRole: ACTION_ROLE.PROJECT,
	info: null,
};

const INIT_TOAST_CONFIG = {
	isVisible: false,
	type: null,
	msg: '',
	isPersistent: false,
};

// Creating context
export const CmicContext = createContext({
	userInfo: INIT_USER_INFO,
	nodeData: {},
	// activeNode: CMIC_STATIC_TREE_NODE_ID.START_NODE,
	activeNode: null,
	currentOfficeTheme: CURRENT_OFFICE_THEME,
	breadcrumbs: [],
	selectedProject: { value: '', label: '' },
	isLoading: false,
	pageInfo: {},
	serverType: {},
	isCloudUser: {},
	showToast: INIT_TOAST_CONFIG,
	setUserInfo: () => { },
	setServerType: () => { },
	setIsCloudUser: () => { },
	handleClick: () => { },
	fetchDataPagination: () => { },
	handleBreadCrumbClicked: () => { },
	setSelectedProject: () => { },
	resetCMiCData: () => { },
	setShowToast: () => { },
	fetchProjects: () => { },
	setIsLoading: () => { },
	fetchDataPagination: () => { },
	removeToast: () => { },
});

const CmicCtxt = ({ currentTheme = CURRENT_OFFICE_THEME, children }) => {
	const [nodes, setNodes] = useState({});
	const [activeNode, setActiveNode] = useState(null);
	const [userInfo, setUserInfo] = useState(INIT_USER_INFO);
	const [selectedProject, setSelectedProject] = useState({ value: '', label: '' });
	const [breadcrumbs, setBreadCrumbs] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	const [showToast, setShowToast] = useState(INIT_TOAST_CONFIG);
	const [pageInfo, setPageInfo] = useState({});
	const [isCloudUser, setIsCloudUser] = useState(true);
	const [serverType, setServerType] = useState({
		data: {},
		err: '',
	});

	useEffect(() => {
		// GETTING USER INFO
		const encodedUserInfo = localStorage.getItem(CMIC_KEYS.SERVER_SETTINGS_ENCRYPTION_KEY);
		// const encodedSelectedProj = localStorage.getItem(CMIC_KEYS.SELCTED_PROJ_ENCRYPTION_KEY);

		let decryptedUserInfo = INIT_USER_INFO;
		// let decryptedSelectedProj = { value: "", label: "" };

		if (encodedUserInfo) {
			decryptedUserInfo = handleEncryption(encodedUserInfo);
		}

		// if (encodedSelectedProj) {
		//   decryptedSelectedProj = handleEncryption(encodedSelectedProj);
		// }

		const requiredUserInfo = {
			username: decryptedUserInfo.username,
			tenantID: decryptedUserInfo.tenantID,
			password: decryptedUserInfo.password,
			serverType: decryptedUserInfo.serverType,
			serverURL: decryptedUserInfo.serverURL,
			usernameAndTenantId: getUsernameAndTenantId(decryptedUserInfo.tenantID, decryptedUserInfo.username),
			environmentName: getEnvironmentNameFromWSURL(decryptedUserInfo.serverURL),
			isTreeShown: decryptedUserInfo.isTreeShown,
			actionRole: decryptedUserInfo.actionRole || ACTION_ROLE.ECM,
			info: decryptedUserInfo.info,
			accessToken: decryptedUserInfo?.accessToken,
			isCloudUser: decryptedUserInfo?.isCloudUser,
			isEnterpriseOAuth: decryptedUserInfo?.isEnterpriseOAuth,
		};

		// todo bring back?
		// setServerType({
		// 	data: requiredUserInfo.serverType,
		// 	err: '',
		// });

		setUserInfo(requiredUserInfo);
		// Code to set received data
		// setSelectedProject(decryptedSelectedProj);
	}, []);

	useEffect(() => {
		if (showToast.isVisible) {
			handleToastTimer();
		}
	}, [showToast.isVisible]);

	const handleToastTimer = () => {
		if (showToast.type === MessageBarType.error || showToast.isPersistent) return;

		setTimeout(() => {
			setShowToast(INIT_TOAST_CONFIG);
		}, [3000]);
	};

	const removeToast = time => {
		return setTimeout(() => {
			setShowToast(INIT_TOAST_CONFIG);
		}, time);
	};

	const handleEncryption = encodedString => {
		const decodeBase64 = Buffer.from(encodedString, 'base64').toString('ascii');
		const bytes = TripleDES.decrypt(decodeBase64, CMIC_KEYS.SERVER_SETTINGS_ENCRYPTION_KEY);
		return JSON.parse(bytes.toString(enc.Utf8));
	};

	const resetCMiCData = () => {
		setNodes({});
		setBreadCrumbs([]);
		setActiveNode(null);
		setIsLoading(false);
	};

	const fetchProjects = useCallback((data = {}, info = userInfo) => {
		return new Promise(async (resolve, reject) => {
			try {
				const result = await fetchData(data, CMIC_TREE_NODE_TYPES.PROJECTS_PARENT, info);

				const resultingData = result?.mainData || { items: [], hasMore: false, totalResults: 0 };

				const requiredData = resultingData.items.map(proj => {
					return {
						value: proj.nodeId,
						// key: proj.nodeId,
						// text: proj.name,
						label: proj.name,
						...proj,
					};
				});

				resolve(requiredData);
			} catch (error) {
				console.log('fetchProjects Error=', error);
				setShowToast({
					isVisible: true,
					msg: 'Something went wrong!',
					type: MessageBarType.error,
					isPersistent: true,
				});
				resolve(false);
			}
		});
	}, []);

	const fetchDataPagination = async (nodeType, config) => {
		let result = {};
		try {
			setIsLoading(true);
			if (pageInfo[nodeType]) {
				result = await fetchData({ ...pageInfo[nodeType], ...config }, nodeType, {
					...userInfo,
					...selectedProject,
				});
				setPageInfo(prev => ({
					...prev,
					[nodeType]: {
						...prev[nodeType],
						...config,
					},
				}));
			} else {
				result = await fetchData({ ...config }, nodeType, { ...userInfo, ...selectedProject });
				setPageInfo(prev => ({
					...prev,
					[nodeType]: {
						...config,
					},
				}));
			}

			if (!result || !result.mainData) {
				setNodes(prev => ({
					...prev,
					[nodeType]: {
						...prev[nodeType],
						items: [],
					},
				}));
				setIsLoading(false);
			} else {
				setNodes(prev => ({
					...prev,
					[nodeType]: result?.mainData,
				}));
				setIsLoading(false);
			}
		} catch (error) {
			console.log('fetchDataPagination() Error', error);
		}
	};

	const handleClick = useCallback(
		async (data, nodeType) => {
			try {
				if (nodeType === CMIC_TREE_NODE_TYPES.PROJECT_DOCUMENT) return;

				setIsLoading(true);
				let result = null;

				//  Temp code for sorting dates
				if (nodeType === CMIC_TREE_NODE_TYPES.PROJECT_FOLDER_RFI) {
					result = await fetchData({ ...data, orderBy: 'PmscrfiCreateDate:desc' }, nodeType, {
						...userInfo,
						...selectedProject,
					});
				} else if (nodeType === CMIC_TREE_NODE_TYPES.PROJECT_FOLDER_ISSUE) {
					result = await fetchData({ ...data, orderBy: 'DmiIuCreateDate:desc' }, nodeType, {
						...userInfo,
						...selectedProject,
					});
				} else if (nodeType === CMIC_TREE_NODE_TYPES.PROJECT_FOLDER_DAILY_JOURNAL) {
					result = await fetchData({ ...data, orderBy: 'PmjDate:desc' }, nodeType, {
						...userInfo,
						...selectedProject,
					});
				} else if (nodeType === CMIC_TREE_NODE_TYPES.PROJECT_FOLDER_SUBMITTALS) {
					result = await fetchData({ ...data, orderBy: 'PmsmSbmtId:desc' }, nodeType, {
						...userInfo,
						...selectedProject,
					});
				} else {
					result = await fetchData(data, nodeType, { ...userInfo, ...selectedProject });
				}
				if (!result || result.noData || result.length === 0 || result.mainData.length === 0) {
					const brdCrmbs = handleBreadCrumbsAddittion(
						nodeType,
						data?.name || data?.data?.name || '-',
						breadcrumbs,
					);

					setIsLoading(false);
					setNodes(prev => ({
						...prev,
						[nodeType]: result,
					}));
					setActiveNode(nodeType);
					setBreadCrumbs(brdCrmbs);
					setPageInfo(prev => ({
						...prev,
						[nodeType]: {
							num: 0,
							nodeId: data.nodeId,
							orderBy: null,
							filter: '',
						},
					}));

					return;
				}

				if (checkParentAddittion(nodeType)) {
					setNodes(prev => ({
						...prev,
						[nodeType]: [{ ...data, children: [] }, ...result?.mainData?.items],
					}));
				} else {
					setNodes(prev => ({
						...prev,
						[nodeType]: result?.mainData,
					}));
				}

				const brdCrmbs = handleBreadCrumbsAddittion(
					nodeType,
					data?.name || data?.data?.name || '-',
					breadcrumbs,
				);
				setActiveNode(nodeType);
				setBreadCrumbs(brdCrmbs);
				setPageInfo(prev => ({
					...prev,
					[nodeType]: {
						num: 0,
						nodeId: data.nodeId,
						orderBy: null,
						filter: '',
					},
				}));
				setIsLoading(false);
			} catch (error) {
				console.log('handleClick() Error=', error);
				setShowToast({
					isVisible: true,
					msg: 'Something went wrong!',
					type: MessageBarType.error,
					isPersistent: true,
				});
			}
		},
		[userInfo, breadcrumbs, selectedProject],
	);

	const handleBreadCrumbClicked = useCallback(
		data => {
			if (data?.id === activeNode) return;
			// Creating copy to avoid unecessary data loosing
			const breadCrumbs = [...breadcrumbs];

			// Finding index to do actual task
			const requiredIndex = breadCrumbs.findIndex(e => e?.id === data?.id);

			// Stopping if we didn't found required node OR User Clicked last node
			if (requiredIndex === -1 || requiredIndex === breadCrumbs.length - 1) return;

			// Filtering data
			const remainingCrumbs = breadCrumbs.splice(requiredIndex + 1);

			filterNodeDataWithCrumbs(remainingCrumbs);
			setBreadCrumbs(breadCrumbs);
			setActiveNode(data?.id);
		},
		[breadcrumbs, activeNode],
	);

	const filterNodeDataWithCrumbs = (crumbs = []) => {
		// Removing extra data from nodes
		const nodeData = { ...nodes };
		const pageInfoData = { ...pageInfo };

		crumbs.forEach(item => {
			if (nodeData[item?.id]) delete nodeData[item?.id];
			if (pageInfoData[item?.id]) delete pageInfoData[item?.id];
		});

		setNodes(nodeData);
		setPageInfo(pageInfoData);
	};

	return (
		<CmicContext.Provider
			value={{
				nodeData: nodes[activeNode],
				selectedProject,
				activeNode,
				breadcrumbs,
				userInfo,
				currentOfficeTheme: currentTheme,
				isLoading,
				showToast,
				pageInfo,
				serverType,
				isCloudUser,
				setShowToast,
				setUserInfo,
				setServerType,
				setIsCloudUser,
				handleClick,
				handleBreadCrumbClicked,
				setSelectedProject,
				fetchProjects,
				fetchDataPagination,
				resetCMiCData,
				setIsLoading,
				removeToast,
			}}
		>
			{children}
		</CmicContext.Provider>
	);
};

export default CmicCtxt;
